import { AfterViewChecked, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { RoleHelper } from '../../helpers/role.helper';
import { AssistantConversation, AssistantMessage } from '../../models/assistant.model';
import { AssistantService } from '../../services/assistant.service';

/**
 * Ratko, AI asistent: plutajuće dugme dole desno i panel koji klizi sa desne
 * strane preko sadržaja. Renderuje ga HeaderComponent, pa postoji na svakoj
 * prijavljenoj stranici i ne postoji na prijavi. Vidljiv samo administratorima;
 * backend permisiju proverava nezavisno od ovoga.
 */
@Component({
  standalone: false,
  selector: 'app-assistant',
  templateUrl: './assistant.component.html',
  styleUrls: ['./assistant.component.scss'],
})
export class AssistantComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesEl') messagesEl: ElementRef<HTMLDivElement>;
  @ViewChild('inputEl') inputEl: ElementRef<HTMLTextAreaElement>;

  readonly suggestions = [
    'Uporedi planirano i ostvareno vreme po operacijama za nalog …',
    'Koji nalozi su za klijenta … i u kojoj su fazi?',
    'Uporedi planiranu i potrošenu količinu materijala za nalog …',
  ];

  /** Širina panela u px; korisnik je menja prevlačenjem leve ivice, pamti se u browseru. */
  static readonly DEFAULT_WIDTH = 520;
  static readonly MIN_WIDTH = 380;
  private static readonly WIDTH_KEY = 'AssistantPanelWidth';

  isVisible = false;
  isOpen = false;
  width = AssistantComponent.DEFAULT_WIDTH;
  isResizing = false;
  isStreaming = false;
  messages: Array<AssistantMessage> = [];
  draft = '';

  /** Lista prethodnih razgovora preklapa poruke dok je otvorena. */
  isHistoryOpen = false;
  isHistoryLoading = false;
  historyError = '';
  conversations: Array<AssistantConversation> = [];
  openConversationId: string | null = null;
  /** Razgovor koji čeka potvrdu brisanja; null kad modal nije otvoren. */
  conversationToDelete: AssistantConversation | null = null;

  private shouldScroll = false;
  private readonly subscriptions = new Subscription();

  constructor(private assistantService: AssistantService, private roleHelper: RoleHelper) {}

  ngOnInit(): void {
    this.isVisible = this.roleHelper.isAdmin();
    if (!this.isVisible) {
      return;
    }
    this.width = this.readSavedWidth();
    this.subscriptions.add(
      this.assistantService.messages$.subscribe(messages => {
        this.messages = messages;
        this.shouldScroll = true;
      })
    );
    this.subscriptions.add(this.assistantService.streaming$.subscribe(streaming => (this.isStreaming = streaming)));
    this.subscriptions.add(
      this.assistantService.open$.subscribe(open => {
        this.isOpen = open;
        if (open) {
          setTimeout(() => this.inputEl?.nativeElement.focus());
        }
      })
    );
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.messagesEl) {
      const el = this.messagesEl.nativeElement;
      el.scrollTop = el.scrollHeight;
      this.shouldScroll = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  open(): void {
    this.assistantService.setOpen(true);
  }

  close(): void {
    this.assistantService.setOpen(false);
  }

  newConversation(): void {
    this.assistantService.newConversation();
    this.isHistoryOpen = false;
    this.openConversationId = null;
    // setTimeout jer se polje za kucanje tek vraća u DOM kad se lista zatvori.
    setTimeout(() => this.inputEl?.nativeElement.focus());
  }

  // ---------- prethodni razgovori ----------

  toggleHistory(): void {
    this.isHistoryOpen = !this.isHistoryOpen;
    if (this.isHistoryOpen) {
      this.loadConversations();
    }
  }

  /**
   * Lista se povlači pri svakom otvaranju: razgovor je mogao nastati u drugom
   * tabu, a i naslov se upisuje tek pri prvom odgovoru servera.
   */
  loadConversations(): void {
    this.isHistoryLoading = true;
    this.historyError = '';
    this.openConversationId = this.assistantService.openConversationId;
    this.subscriptions.add(
      this.assistantService.listConversations().subscribe({
        next: list => {
          this.conversations = list.conversations;
          this.isHistoryLoading = false;
        },
        error: () => {
          this.historyError = 'Nije moguće učitati razgovore.';
          this.isHistoryLoading = false;
        },
      })
    );
  }

  openConversation(conversation: AssistantConversation): void {
    // Dok odgovor stiže, učitavanje drugog razgovora bi pregazilo poruke u toku.
    // Drugi klik dok prvi još traje bi se utrkivao: odlučivao bi red kojim
    // odgovori stignu, ne red kojim je kliknuto.
    if (this.isStreaming || this.isHistoryLoading) {
      return;
    }
    this.isHistoryLoading = true;
    this.historyError = '';
    this.subscriptions.add(
      this.assistantService.openConversation(conversation.conversationId).subscribe({
        next: () => {
          this.openConversationId = conversation.conversationId;
          this.isHistoryOpen = false;
          this.isHistoryLoading = false;
          setTimeout(() => this.inputEl?.nativeElement.focus());
        },
        error: () => {
          // Najčešće je razgovor u međuvremenu istekao ili obrisan drugde.
          this.historyError = 'Razgovor više ne postoji.';
          this.isHistoryLoading = false;
          this.loadConversations();
        },
      })
    );
  }

  askDelete(conversation: AssistantConversation): void {
    this.conversationToDelete = conversation;
  }

  onDeleteAnswer(confirmed: boolean): void {
    const conversation = this.conversationToDelete;
    this.conversationToDelete = null;
    if (confirmed && conversation) {
      this.deleteConversation(conversation);
    }
  }

  private deleteConversation(conversation: AssistantConversation): void {
    this.subscriptions.add(
      this.assistantService.deleteConversation(conversation.conversationId).subscribe({
        next: () => {
          this.conversations = this.conversations.filter(c => c.conversationId !== conversation.conversationId);
          // Ako je obrisan baš otvoreni razgovor, panel prelazi na prazan.
          this.assistantService.forgetIfOpen(conversation.conversationId);
          this.openConversationId = this.assistantService.openConversationId;
        },
        error: () => {
          this.historyError = 'Brisanje nije uspelo.';
        },
      })
    );
  }

  /** Naslov se upisuje tek pri prvom odgovoru, pa novi razgovor može biti bez njega. */
  conversationTitle(conversation: AssistantConversation): string {
    return conversation.title?.trim() || 'Razgovor bez naslova';
  }

  trackByConversation(index: number, conversation: AssistantConversation): string {
    return conversation.conversationId;
  }

  onSubmit(): void {
    // Dok je lista otvorena polja za kucanje nema, pa poruka nema gde da se
    // prikaže: ranije se slala i odgovor je stizao iza liste, nevidljiv.
    if (this.isHistoryOpen) {
      return;
    }
    if (this.isStreaming) {
      this.assistantService.stop();
      return;
    }
    const text = this.draft.trim();
    if (!text) {
      return;
    }
    this.assistantService.send(text);
    this.draft = '';
    this.resizeInput();
  }

  useSuggestion(text: string): void {
    // Predlozi sadrže mesto za nalog/klijenta; korisnik ga dopuni pre slanja.
    this.draft = text.replace(' …', ' ').replace('…', '');
    this.inputEl?.nativeElement.focus();
    setTimeout(() => this.resizeInput());
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSubmit();
    }
    if (event.key === 'Escape') {
      if (this.isHistoryOpen) {
        this.isHistoryOpen = false;
        return;
      }
      this.close();
    }
  }

  resizeInput(): void {
    const el = this.inputEl?.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${Math.min(140, el.scrollHeight)}px`;
  }

  // ---------- promena širine prevlačenjem leve ivice ----------

  startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    document.body.style.userSelect = 'none';
  }

  @HostListener('document:mousemove', ['$event'])
  onResize(event: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }
    this.width = this.clampWidth(window.innerWidth - event.clientX);
  }

  @HostListener('document:mouseup')
  stopResize(): void {
    if (!this.isResizing) {
      return;
    }
    this.isResizing = false;
    document.body.style.userSelect = '';
    this.saveWidth();
  }

  resetWidth(): void {
    this.width = AssistantComponent.DEFAULT_WIDTH;
    this.saveWidth();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.width = this.clampWidth(this.width);
  }

  private clampWidth(width: number): number {
    const max = Math.max(AssistantComponent.MIN_WIDTH, window.innerWidth - 240);
    return Math.round(Math.min(max, Math.max(AssistantComponent.MIN_WIDTH, width)));
  }

  private readSavedWidth(): number {
    try {
      const saved = Number(localStorage.getItem(AssistantComponent.WIDTH_KEY));
      return saved ? this.clampWidth(saved) : AssistantComponent.DEFAULT_WIDTH;
    } catch {
      return AssistantComponent.DEFAULT_WIDTH;
    }
  }

  private saveWidth(): void {
    try {
      localStorage.setItem(AssistantComponent.WIDTH_KEY, String(this.width));
    } catch {
      // localStorage nedostupan: širina važi samo za ovu sesiju
    }
  }

  /** Poslednji korak je aktivan dok odgovor traje i još nema teksta. */
  isStepActive(message: AssistantMessage, index: number): boolean {
    return message.streaming && !message.text && index === message.steps.length - 1;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
